/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  LexicalTypeaheadMenuPlugin,
  MenuTextMatch,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { TextNode } from "lexical";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { CustomFieldKey, CustomFields } from "entities/domain/templates";
import { Flex, List, ListItem, Text, useColorMode } from "@chakra-ui/react";

import { $createCustomFieldNode } from "../nodes/CustomFieldNode";

const PUNCTUATION =
  "\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%'\"~=<>_:;";
const NAME = "\\b[A-Z][^\\s" + PUNCTUATION + "]";

const DocumentCustomFieldsRegex = {
  NAME,
  PUNCTUATION,
};

const CapitalizedNameCustomFieldsRegex = new RegExp(
  "(^|[^#])((?:" + DocumentCustomFieldsRegex.NAME + "{" + 1 + ",})$)"
);

const PUNC = DocumentCustomFieldsRegex.PUNCTUATION;

const TRIGGERS = ["{"].join("");

// Chars we expect to see in a custom field (non-space, non-punctuation).
const VALID_CHARS = "[^" + TRIGGERS + PUNC + "\\s]";

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  "(?:" +
  "\\.[ |$]|" + // E.g. "r. " in "Mr. Smith"
  " |" + // E.g. " " in "Josh Duck"
  "[" +
  PUNC +
  "]|" + // E.g. "-' in "Salier-Hellendag"
  ")";

const LENGTH_LIMIT = 75;

const FigureBraceCustomFieldsRegex = new RegExp(
  "(^|\\s|\\()(" +
    "[" +
    TRIGGERS +
    "]" +
    "((?:" +
    VALID_CHARS +
    VALID_JOINS +
    "){0," +
    LENGTH_LIMIT +
    "})" +
    ")$"
);

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50;

// Regex used to match alias.
const FigureBraceCustomFieldsRegexAliasRegex = new RegExp(
  "(^|\\s|\\()(" +
    "[" +
    TRIGGERS +
    "]" +
    "((?:" +
    VALID_CHARS +
    "){0," +
    ALIAS_LENGTH_LIMIT +
    "})" +
    ")$"
);

const SUGGESTION_LIST_LENGTH_LIMIT = 3;

const customFieldsCache = new Map();

const lookupService = {
  search(
    string: string,
    customFields: CustomFields,
    callback: (results: Array<{ key: string; value: string }>) => void
  ): void {
    setTimeout(() => {
      const results = Object.keys(customFields)
        .filter((customFieldKey) =>
          customFieldKey.toLowerCase().includes(string.toLowerCase())
        )
        .map((customFieldKey) => ({
          key: customFieldKey,
          value: customFields[customFieldKey as CustomFieldKey]!,
        }));
      callback(results);
    }, 500);
  },
};

function useCustomFieldLookupService(
  customFieldSearchString: string | null,
  customFields: CustomFields
) {
  const [results, setResults] = useState<
    Array<{
      key: string;
      value: string;
    }>
  >([]);

  useEffect(() => {
    const cachedResults = customFieldsCache.get(customFieldSearchString);

    if (customFieldSearchString == null) {
      setResults([]);
      return;
    }

    if (cachedResults === null) {
      return;
    } else if (cachedResults !== undefined) {
      setResults(cachedResults);
      return;
    }

    customFieldsCache.set(customFieldSearchString, null);
    lookupService.search(
      customFieldSearchString,
      customFields,
      (newResults) => {
        customFieldsCache.set(customFieldSearchString, newResults);
        setResults(newResults);
      }
    );
  }, [customFieldSearchString, customFields]);

  return results;
}

function checkForCapitalizedNameCustomFields(
  text: string,
  minMatchLength: number
): MenuTextMatch | null {
  const match = CapitalizedNameCustomFieldsRegex.exec(text);
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1];

    const matchingString = match[2];
    if (matchingString != null && matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: matchingString,
      };
    }
  }
  return null;
}

function checkForAtSignCustomFields(
  text: string,
  minMatchLength: number
): MenuTextMatch | null {
  let match = FigureBraceCustomFieldsRegex.exec(text);

  if (match === null) {
    match = FigureBraceCustomFieldsRegexAliasRegex.exec(text);
  }

  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1];

    const matchingString = match[3];
    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: match[2],
      };
    }
  }
  return null;
}

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
  let match = checkForAtSignCustomFields(text, 1);

  if (match === null) {
    match = checkForCapitalizedNameCustomFields(text, 3);
  }

  return match;
}

class CustomFieldTypeaheadOption extends MenuOption {
  name: string;
  actualText: string;

  constructor(name: string, actualText: string) {
    super(name);
    this.name = name;
    this.actualText = actualText;
  }
}

function humanize(str: string) {
  return str
    .replace(/_/g, " ")
    .replace(/[{}]/g, "")
    .replace(/(?: |\b)(\w)/g, (key) => key.toUpperCase());
}

function CustomFieldsTypeaheadMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: CustomFieldTypeaheadOption;
}) {
  let className = "item";
  if (isSelected) {
    className += " selected";
  }

  const { colorMode } = useColorMode();

  return (
    <ListItem
      px={2}
      key={option.key}
      tabIndex={-1}
      className={className}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={"typeahead-item-" + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      cursor="pointer"
      width="100%"
      borderTopLeftRadius={index === 0 ? "md" : "none"}
      borderTopRightRadius={index === 0 ? "md" : "none"}
      borderBottomLeftRadius={
        index === SUGGESTION_LIST_LENGTH_LIMIT - 1 ? "md" : "none"
      }
      borderBottomRightRadius={
        index === SUGGESTION_LIST_LENGTH_LIMIT - 1 ? "md" : "none"
      }
      _hover={{
        backgroundColor: colorMode === "dark" ? "gray.500" : "gray.50",
      }}
    >
      <Text width="max-content">{humanize(option.actualText)}</Text>
    </ListItem>
  );
}

export default function CustomFieldTypeaheadPlugin({
  customFields,
  highlightUnknownCustomFields,
  openEditCustomFieldModal,
}: {
  customFields: CustomFields;
  highlightUnknownCustomFields: boolean;
  openEditCustomFieldModal?: (key: string, value: string) => void;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  const [queryString, setQueryString] = useState<string | null>(null);

  const results = useCustomFieldLookupService(queryString, customFields);

  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch("/", {
    minLength: 0,
  });

  const options = useMemo(
    () =>
      results
        .map(
          (result) =>
            new CustomFieldTypeaheadOption(result.value, `{${result.key}}`)
        )
        .slice(0, SUGGESTION_LIST_LENGTH_LIMIT),
    [results]
  );

  const onSelectOption = useCallback(
    (
      selectedOption: CustomFieldTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      editor.update(() => {
        const customFieldNode = $createCustomFieldNode(
          selectedOption.name,
          selectedOption.actualText,
          highlightUnknownCustomFields,
          openEditCustomFieldModal
        );
        if (nodeToReplace) {
          nodeToReplace.replace(customFieldNode);
        }
        customFieldNode.select();
        closeMenu();
      });
    },
    [editor, customFields]
  );

  const checkForCustomFieldMatch = useCallback(
    (text: string) => {
      const customFieldMatch = getPossibleQueryMatch(text);
      const slashMatch = checkForSlashTriggerMatch(text, editor);
      return !slashMatch && customFieldMatch ? customFieldMatch : null;
    },
    [checkForSlashTriggerMatch, editor, customFields]
  );

  const { colorMode } = useColorMode();

  return (
    <LexicalTypeaheadMenuPlugin<CustomFieldTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForCustomFieldMatch}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ) => {
        return anchorElementRef.current && results.length
          ? ReactDOM.createPortal(
              <Flex position="relative" zIndex={50} marginTop="-5rem">
                <List
                  spacing={1}
                  borderRadius="md"
                  borderStyle="solid"
                  borderWidth="1px"
                  borderColor="gray.50"
                  background={colorMode === "dark" ? "gray.700" : "white"}
                >
                  {options.map((option, i: number) => (
                    <CustomFieldsTypeaheadMenuItem
                      index={i}
                      isSelected={selectedIndex === i}
                      onClick={() => {
                        setHighlightedIndex(i);
                        selectOptionAndCleanUp(option);
                      }}
                      onMouseEnter={() => {
                        setHighlightedIndex(i);
                      }}
                      key={option.key}
                      option={option}
                    />
                  ))}
                </List>
              </Flex>,
              anchorElementRef.current
            )
          : null;
      }}
    />
  );
}
